import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useReferralCode } from "../services/referral.api";
import { Card } from "@/shared/components/ui/card";

export default function AffiliateProgram() {
  const { data: referralCodeData, isLoading: isLoadingCode } =
    useReferralCode();
  // Hidden sections: auto-withdraw and earnings breakdown removed for now.
  const isLoading = isLoadingCode;

  // Construct referral link from referral code
  const siteUrl = window.location.origin;
  const referralLink = referralCodeData?.referralCode
    ? `${siteUrl}/r/${referralCodeData.referralCode}`
    : "";

  const handleCopyLink = () => {
    if (referralLink) {
      navigator.clipboard.writeText(referralLink);
      toast.success("Referral link copied to clipboard");
    }
  };

  const handleCopyCode = () => {
    const code = referralCodeData?.referralCode;
    if (code) {
      navigator.clipboard.writeText(code);
      toast.success("Referral code copied to clipboard");
    }
  };

  const handleShareWhatsapp = () => {
    if (!referralLink) return;
    const url = `https://wa.me/?text=${encodeURIComponent(referralLink)}`;
    window.open(url, "_blank", "noopener");
  };

  const handleShareFacebook = () => {
    if (!referralLink) return;
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`;
    window.open(url, "_blank", "noopener");
  };

  const handleShareTelegram = () => {
    if (!referralLink) return;
    const url = `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent("Join me on Nommia")}`;
    window.open(url, "_blank", "noopener");
  };

  const handleShareInstagram = () => {
    if (!referralLink) return;
    navigator.clipboard.writeText(referralLink);
    toast.success("Referral link copied. Paste it into Instagram DM or bio.");
  };

  const handleShareOther = () => {
    if (!referralLink) return;
    const mailto = `mailto:?subject=${encodeURIComponent("Join me on Nommia")}&body=${encodeURIComponent(referralLink)}`;
    window.open(mailto, "_blank", "noopener");
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#E0B968]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white pb-20 md:pb-0 -ml-6 -mt-6 -mr-6">
      {/* Header */}
      <div className="relative h-50 w-full overflow-hidden">
        <div className="absolute inset-0 bg-[url('/assets/pattern.png')] opacity-10"></div>
        <div className="relative z-10 p-4">
          <div className="text-center mt-4">
            <h1 className="text-2xl font-bold text-white">
              Refer and Earn Rewards
            </h1>
            <p className="text-sm text-white/80">
              Share the benefits and get rewarded
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-20 relative z-20">
        {/* How it works Card */}
        <Card className="bg-primary/5 border-none p-6 text-center mb-6">
          <h3 className="text-white font-medium">How it works</h3>
          <div className="flex justify-between items-start">
            <div className="flex flex-col items-center w-1/3">
              <img
                src="/assets/icons/refer-1-icon.svg"
                alt="refer"
                className="h-12 w-12 mb-2"
              />
              <p className="text-xs text-gray-400">Refer a friend</p>
            </div>
            <div className="flex flex-col items-center w-1/3">
              <img
                src="/assets/icons/refer-2-icon.svg"
                alt="complete action"
                className="h-12 w-12 mb-2"
              />
              <p className="text-xs text-gray-400">
                Your friend complete their action
              </p>
            </div>
            <div className="flex flex-col items-center w-1/3">
              <img
                src="/assets/icons/refer-3-icon.svg"
                alt="reward"
                className="h-12 w-12 mb-2"
              />
              <p className="text-xs text-gray-400">Earn your Reward!</p>
            </div>
          </div>
        </Card>

        {/* Invite via */}
        <div className="mb-6">
          <h3 className="text-sm font-medium mb-3">Invite via</h3>
          <div className="space-y-3">
            <div className="bg-primary/5 p-4 rounded-xl flex items-center justify-between">
              <span className="text-sm font-medium">Referral Link</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 truncate max-w-[150px]">
                  {referralLink}
                </span>
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
              </div>
            </div>

            <div className="bg-primary/5 p-4 rounded-xl flex items-center justify-between">
              <span className="text-sm font-medium">Referral Code</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  {referralCodeData?.referralCode || "—"}
                </span>
                <button
                  onClick={handleCopyCode}
                  className="flex items-center gap-1 text-xs text-primary hover:underline">
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Share to */}
        <div className="mb-8">
          <h3 className="text-sm font-medium mb-3">Share to</h3>
          <div className="flex justify-between px-2">
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleShareWhatsapp}
                className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition">
                <img
                  src="/assets/icons/whatsapp-icon.svg"
                  alt="WhatsApp"
                  className="h-6 w-6"
                />
              </button>
              <span className="text-xs">WhatsApp</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleShareFacebook}
                className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition">
                <img
                  src="/assets/icons/facebook-icon.svg"
                  alt="Facebook"
                  className="h-6 w-6"
                />
              </button>
              <span className="text-xs">Facebook</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleShareTelegram}
                className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition">
                <img
                  src="/assets/icons/telegram-icon.svg"
                  alt="Telegram"
                  className="h-6 w-6"
                />
              </button>
              <span className="text-xs">Telegram</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleShareInstagram}
                className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition">
                <img
                  src="/assets/icons/instagram-icon.svg"
                  alt="Instagram"
                  className="h-6 w-6"
                />
              </button>
              <span className="text-xs">Instagram</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleShareOther}
                className="h-12 w-12 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition">
                <img
                  src="/assets/icons/other-icon.svg"
                  alt="Other"
                  className="h-6 w-6"
                />
              </button>
              <span className="text-xs">more</span>
            </div>
          </div>
        </div>

        {/* Stats hidden for now */}

        {/* Auto withdraw and earnings breakdown hidden for now */}
      </div>
    </div>
  );
}
